<template>
  <AppWithLoading :fetch="fetchAll">
    <div :class="wrapClass">
      <slot name="head" v-bind="{activeMethod}" />
      <VMenu offset-y v-if="activeMethod">
        <template v-slot:activator="{on,attrs}">
          <div class="payment-method-wrap">
            <slot name="title">
              <div class="payment-method-title">
                {{ $t('paymentMethod') }}:
              </div>
            </slot>
            <slot name="activator" v-bind="{on,attrs,activeMethod}">
              <div class="card-wrap-mini selected">
                <div>
                  <div class="d-flex" style="align-items: center">
                    <PaymentMethodCardBrand v-if="activeMethod.data" class="card-wrap-mini__brand-wrap" :brand="activeMethod.data.brand" />
                    <div class="d-flex align-center">
                      <div class="card-wrap-mini__name">
                        <span> {{ activeMethod.title }}</span>
                        <VTooltip bottom nudge-right="92">
                          <template v-slot:activator="{ on, attrs }">
                            <VIcon
                              :title="$t('failed',{failedAt: formatDateDefault(activeMethod.failedAt)})"
                              v-if="activeMethod.failedAt"
                              class="card-wrap-mini__icon"
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-alert
                            </VIcon>
                          </template>
                          <span>{{ $t('failed',{failedAt: formatDateDefault(activeMethod.failedAt)}) }}</span>
                        </VTooltip>
                      </div>
                    </div>
                  </div>
                </div>
                <a
                  v-bind="attrs"
                  v-on="on"
                  class="card-wrap-mini__change"
                >
                  {{ $t('change') }}
                </a>
              </div>
            </slot>
          </div>
        </template>
        <VCard>
          <VCardText>
            <VRadioGroup v-model="active">
              <VRadio
                v-for="(method, index) in paymentMethods"
                :key="index"
                :value="method.id"
              >
                <template #label>
                  <PaymentMethodCardBrand :brand="method.data.brand" />
                  <div class="card-wrap-mini__name">
                    <VIcon
                      :title="$t('failed',{failedAt: formatDateDefault(method.failedAt)})"
                      v-if="method.failedAt"
                      class="card-wrap-mini__icon"
                    >
                      mdi-alert
                    </VIcon>
                    {{ method.title }}
                  </div>
                </template>
              </VRadio>
              <VRadio value="new" :label="$t('addNewCard')" />
            </VRadioGroup>
          </VCardText>
        </VCard>
      </VMenu>
    </div>
    <slot name="footer" v-bind="{activeMethod}" />
  </AppWithLoading>
</template>

<script>
import usePaymentMethods from '@/compositions/accounting/usePaymentMethods';
import {
  computed, ref, watch, watchEffect,
} from '@vue/composition-api';
import useDateFormatter from '@/compositions/useDateFormatter';
import { useI18n } from 'vue-i18n-composable';
import PaymentMethodCardBrand from '@/components/Payment/Methods/PaymentMethodCardBrand';
import AppWithLoading from '@/components/AppWithLoading';

export default {
  name: 'PaymentMethodsCardSelector',
  components: { AppWithLoading, PaymentMethodCardBrand },
  props: {
    wrapClass: {
      type: String,
      default: null,
    },
  },
  setup(_, { emit }) {
    const { t } = useI18n();
    const { fetchAll, paymentMethods } = usePaymentMethods();
    const active = ref(null);
    const change = ref(false);

    const activeMethod = computed(() => {
      if (active.value === 'new') {
        return {
          title: t('addNewCard'),
        };
      }
      if (active.value) {
        return paymentMethods.value.find((method) => method.id === active.value);
      }

      const activeCard = paymentMethods.value.find((method) => method.active === true);

      return activeCard || paymentMethods.value[0];
    });

    const { formatDateDefault } = useDateFormatter();

    watchEffect(() => {
      emit('input', active.value);
    });

    watch(paymentMethods, () => {
      active.value = activeMethod.value.id;
    });

    return {
      paymentMethods,
      active,
      activeMethod,
      change,
      formatDateDefault,
      fetchAll,
    };
  },
};
</script>

<style lang="scss" scoped>
  .payment-method-wrap {
    max-width: 36.625rem;
  }
  .payment-method-title {
    font-size: 1.0625rem;
    line-height: 1.53;
    color: #091A35;
    margin-bottom: 0.625rem;
  }
  .card-wrap-mini {
    display: flex;
    justify-content: space-between;
    padding: 1.6875rem 1.25rem 1.6875rem 3.25rem;
    border: 1px solid #EEEEEE;
    border-radius: 0.5rem;
    position: relative;
    margin-bottom: 1.25rem;
    &:before {
      content: '';
      width: 1.125rem;
      height: 1.125rem;
      border-radius: 50%;
      border: 2px solid #757575;
      position: absolute;
      top: 1.8125rem;
      left: 1.25rem;
    }
    &.selected {
      border: 2px solid #1FC57F;
      &:before {
        border: 5px solid #0A82D9;;
        top: 1.8125rem;
      }
    }
    &__name {
      display: flex;
      align-items: center;
      margin-left: 1.25rem;
    }
    &__icon {
      display: block;
      color: #EA0D0D;
      font-size: 1.25rem;
      padding-bottom: 3px;
      margin-left: 0.125rem;
    }
  }
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .payment-method-title {
      font-size: 1rem;
      padding: 0 0.625rem;
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .card-wrap-mini {
      display: block;
      padding: 0.5625rem 3.25rem 0.5625rem 2.6875rem;
      margin-bottom: 0.5rem;
      &:before {
        top: 1rem;
        left: 0.9375rem;
      }
      &.selected:before {
        top: 1rem;
      }
      &__icon {
        font-size: 1.125rem;
        margin-left: 0;
      }
      &__name {
        margin-left: 0;
      }
      &__change {
        font-size: 0.8125rem;
      }
      &__brand-wrap {
        position: absolute;
        top: 1.125rem;
        right: 0.625rem;
      }
    }
  }
</style>

<i18n lang="yml" locale="ru">
  paymentMethod: Способ оплаты
  change: Изменить
  addNewCard: Добавить карту
  failed: 'Не удалось выполнить списание с карты {failedAt}'
</i18n>
<i18n lang="yml" locale="en">
  paymentMethod: Payment method
  change: Change
  addNewCard: Add card
  failed: 'Last payment was unsuccessful {failedAt}'
</i18n>
